/**
 * OutlinerConfig.ts - OutlinerConfig.js
 * 
 * CHANGE HISTORY:
 * - 251107_2135 [current] danielbeer: feat: Add OpCentral orchestrator integration
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
/**
 * Column Types
 */
export const COLUMN_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  BOOLEAN: 'boolean',
  SELECT: 'select',
  ICON: 'icon',
  COLOR: 'color',
  ACTIONS: 'actions',
  CUSTOM: 'custom'
};

/**
 * Display Modes
 */
export const DISPLAY_MODES = {
  TREE: 'tree',           // Hierarchical tree with disclosure triangles
  TABLE: 'table',         // Flat table with columns
  TREE_TABLE: 'tree-table' // Hybrid: tree structure with multiple columns
};

/**
 * Selection Modes
 */
export const SELECTION_MODES = {
  NONE: 'none',           // No selection
  SINGLE: 'single',       // Single row selection
  MULTI: 'multi',         // Multiple row selection
  RANGE: 'range'          // Range selection with Shift+Click
};

/**
 * Edit Modes
 */
export const EDIT_MODES = {
  NONE: 'none',           // Read-only
  INLINE: 'inline',       // Inline editing
  MODAL: 'modal',         // Edit in modal/panel
  CELL: 'cell'            // Cell-by-cell editing (like Excel)
};

/**
 * Default Column Configuration
 */
export const DEFAULT_COLUMN_CONFIG = {
  id: null,               // Unique column identifier
  label: '',              // Display label
  field: null,            // Data field name (or function)
  type: COLUMN_TYPES.TEXT,
  width: 150,             // Default width in pixels
  minWidth: 50,
  maxWidth: null,
  resizable: true,
  sortable: true,
  filterable: true,
  editable: false,
  visible: true,
  align: 'left',          // left, center, right
  format: null,           // Formatting function
  render: null,           // Custom render function
  validate: null,         // Validation function
  order: 0                // Display order
};

/**
 * Default Outliner Configuration
 */
export const DEFAULT_OUTLINER_CONFIG = {
  // Display
  displayMode: DISPLAY_MODES.TREE_TABLE,
  showHeader: true,
  showFilters: false,
  showFooter: false,
  
  // Columns
  columns: [],
  primaryColumn: 'name',  // Which column shows the tree structure
  columnOrder: [],        // Custom column order
  
  // Hierarchy
  hierarchyField: 'parentId',     // Field that defines parent relationship
  childrenField: 'children',      // Field that contains children array
  expandedField: 'isExpanded',    // Field that tracks expansion state
  levelField: 'level',            // Field that stores hierarchy level
  hasChildrenField: null,         // Optional: field indicating if node has children
  
  // Selection
  selectionMode: SELECTION_MODES.MULTI,
  selectOnClick: true,
  showCheckboxes: false,
  
  // Editing
  editMode: EDIT_MODES.INLINE,
  editOnDoubleClick: true,
  editOnType: true,
  saveOnBlur: true,
  saveOnEnter: true,
  
  // Keyboard Navigation
  keyboardNavigation: true,
  arrowKeyNavigation: true,
  smartArrowNavigation: true,  // Smart navigation in edit mode
  enterToEdit: true,
  escapeToCancel: true,
  tabToIndent: true,
  
  // Sorting
  sortable: true,
  multiSort: true,
  defaultSort: [],        // [{column: 'name', direction: 'asc'}]
  
  // Filtering
  filterable: true,
  quickFilter: true,      // Global search
  columnFilters: true,    // Per-column filters
  
  // Reordering
  reorderable: true,      // Drag to reorder rows
  reorderColumns: true,   // Drag to reorder columns
  
  // Resizing
  resizableColumns: true,
  autoSizeColumns: false,
  
  // Row Configuration
  rowHeight: 36,
  allowMultiLine: false,
  alternateRowColors: false,
  hoverHighlight: true,
  
  // Tree Configuration
  indentSize: 24,         // Pixels per level
  showDisclosure: true,   // Show expand/collapse triangles
  showLines: true,        // Show hierarchy lines
  expandOnClick: false,   // Expand when clicking row (vs just triangle)
  
  // Actions
  showActions: true,
  actions: [],            // Array of action buttons
  contextMenu: null,      // Context menu configuration
  
  // Data
  data: [],
  idField: 'id',
  
  // Orchestrators (OpCentral Integration)
  actr: null,              // Actr instance for CRUD operations
  interfacr: null,         // Interfacr instance for UI operations
  aggregatr: null,         // Aggregatr instance for persistence
  profylr: null,           // Profylr instance for profile context
  
  // Workspace/Dataset Context (for Aggregatr)
  workspaceId: null,       // Current workspace ID
  datasetId: null,         // Current dataset ID
  
  // Callbacks (deprecated - use orchestrators instead)
  onSelect: null,
  onEdit: null,
  onSave: null,
  onDelete: null,
  onReorder: null,
  onExpand: null,
  onCollapse: null,
  onSort: null,
  onFilter: null,
  
  // Styling
  className: '',
  theme: 'default',
  
  // Performance
  virtualScroll: false,   // Enable for large datasets
  lazyLoad: false,        // Load children on expand
  pageSize: 100,
  
  // Accessibility
  ariaLabel: 'Data Outliner',
  keyboardShortcuts: true
};

/**
 * Keyboard Shortcuts Configuration
 */
export const DEFAULT_KEYBOARD_SHORTCUTS = {
  // Navigation
  'ArrowUp': 'navigateUp',
  'ArrowDown': 'navigateDown',
  'ArrowLeft': 'navigateLeft',
  'ArrowRight': 'navigateRight',
  'Home': 'navigateFirst',
  'End': 'navigateLast',
  
  // Selection
  'Shift+ArrowUp': 'extendSelectionUp',
  'Shift+ArrowDown': 'extendSelectionDown',
  'Cmd+A': 'selectAll',
  'Cmd+Click': 'toggleSelection',
  'Shift+Click': 'rangeSelection',
  
  // Editing
  'Enter': 'saveOrEdit',
  'Cmd+Enter': 'saveAndCreateSibling',
  'Shift+Enter': 'newLine',
  'Escape': 'cancelEdit',
  'Space': 'enterEditMode',
  
  // Tree Operations
  'Tab': 'indent',
  'Shift+Tab': 'outdent',
  'Cmd+ArrowUp': 'moveUp',
  'Cmd+ArrowDown': 'moveDown',
  'Cmd+ArrowLeft': 'outdent',
  'Cmd+ArrowRight': 'indent',
  
  // Actions
  'Cmd+C': 'copy',
  'Cmd+V': 'paste',
  'Cmd+X': 'cut',
  'Cmd+D': 'duplicate',
  'Delete': 'delete',
  'Cmd+Z': 'undo',
  'Cmd+Shift+Z': 'redo'
};

/**
 * Action Button Configuration
 */
export const DEFAULT_ACTION_CONFIG = {
  id: null,
  label: '',
  icon: null,
  tooltip: '',
  onClick: null,
  visible: true,
  enabled: true,
  className: ''
};

/**
 * Create a column configuration
 */
export function createColumn(config) {
  return {
    ...DEFAULT_COLUMN_CONFIG,
    ...config
  };
}

/**
 * Create an outliner configuration
 */
export function createOutlinerConfig(config) {
  return {
    ...DEFAULT_OUTLINER_CONFIG,
    ...config,
    columns: (config.columns || []).map(col => createColumn(col))
  };
}

/**
 * Validate configuration
 */
export function validateConfig(config) {
  const errors = [];
  
  if (!config.idField) {
    errors.push('idField is required');
  }
  
  if (config.displayMode === DISPLAY_MODES.TREE_TABLE && !config.primaryColumn) {
    errors.push('primaryColumn is required for tree-table mode');
  }
  
  if (config.columns.length === 0) {
    errors.push('At least one column is required');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

export default {
  COLUMN_TYPES,
  DISPLAY_MODES,
  SELECTION_MODES,
  EDIT_MODES,
  DEFAULT_COLUMN_CONFIG,
  DEFAULT_OUTLINER_CONFIG,
  DEFAULT_KEYBOARD_SHORTCUTS,
  DEFAULT_ACTION_CONFIG,
  createColumn,
  createOutlinerConfig,
  validateConfig
};
